/**
 * AI Cost Dashboard Page
 *
 * Platform-level analytics for AI pipeline costs
 * Requires special permission: platform.ai_analytics.view
 *
 * Design: Data Observatory - sophisticated data visualization with
 * subtle glassmorphism and clean typography
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useFilterParams } from '@/hooks/useFilterParams';
import { DataTable, ColumnDefinition, SortState } from '@/components/shared/DataTable';
import {
  CurrencyDollarIcon,
  CircleStackIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BuildingOfficeIcon,
  CpuChipIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  SparklesIcon,
  XMarkIcon,
  MicrophoneIcon,
  ClockIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { API_BASE_URL } from '../config/api';


// Types
interface CostSummary {
  totalCost: number;
  totalTokens: number;
  totalRequests: number;
  avgCostPerRequest: number;
  promptTokens: number;
  completionTokens: number;
  periodStart: string;
  periodEnd: string;
  comparison?: {
    previousPeriodCost: number;
    changePercent: number;
    changeDirection: 'up' | 'down' | 'flat';
  };
  // Extended fields from backend
  speechCost?: number;
  openaiCost?: number;
  imoCost?: number;
  audioMinutes?: number;
  encounterCount?: number;
  avgCostPerAppointment?: number;
}

interface OrganizationCost {
  organizationId: string;
  organizationName: string;
  totalCost: number;
  totalTokens: number;
  totalRequests: number;
  percentOfTotal: number;
}

interface ModelCost {
  model: string;
  totalCost: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  totalRequests: number;
  avgCostPerRequest: number;
}

interface PromptCost {
  promptId: string;
  promptName: string;
  category: string;
  totalCost: number;
  totalTokens: number;
  avgPromptTokens: number;
  avgCompletionTokens: number;
  totalRequests: number;
  avgCostPerRequest: number;
  efficiencyScore: number;
}

interface ServiceCost {
  service: string;
  totalCost: number;
  totalRequests: number;
  percentOfTotal: number;
}

interface EncounterCost {
  encounterId?: string;
  appointmentId?: string;
  patientName?: string;
  encounterDate: string;
  totalCost: number;
  speechCost: number;
  openaiCost: number;
  imoCost: number;
  operationBreakdown: {
    transcription: number;
    summarization: number;
    noteGeneration: number;
    coding: number;
    other: number;
  };
  audioMinutes: number;
  totalTokens: number;
  totalRequests: number;
}

interface OperationCost {
  operation: string;
  operationName: string;
  category: string;
  service: string;
  totalCost: number;
  totalRequests: number;
  avgCostPerRequest: number;
  avgTokens: number;
  audioMinutes?: number;
}

interface UserCost {
  userId: string;
  userName: string;
  userRole: string;
  department?: string;
  totalCost: number;
  totalRequests: number;
  encounterCount: number;
  avgCostPerEncounter: number;
  audioMinutes: number;
  percentOfTotal: number;
}

interface TrendDataPoint {
  date: string;
  period: { year: number; month?: number; day?: number };
  totalCost: number;
  totalTokens: number;
  totalRequests: number;
  promptTokens?: number;
  completionTokens?: number;
}

interface PromptDefinition {
  id: string;
  name: string;
  description: string;
  category: string;
  service: string;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  model: string;
  stats?: {
    totalCost: number;
    totalRequests: number;
    avgPromptTokens: number;
    avgCompletionTokens: number;
  };
}

interface HierarchyOrganization {
  id: string;
  name: string;
}
interface HierarchyPartner {
  id: string;
  name: string;
  organizations: HierarchyOrganization[];
}
interface HierarchyEnterprise {
  id: string;
  name: string;
  partners: HierarchyPartner[];
}
interface FilterHierarchy {
  enterprises: HierarchyEnterprise[];
  orphanPartners: HierarchyPartner[];
}

type TimePreset = 'thisMonth' | 'lastMonth' | 'last3Months' | 'thisYear' | 'lastYear' | 'custom';
type Granularity = 'daily' | 'monthly' | 'yearly';
type TabType = 'finance' | 'engineering';

// Dashboard data type (consolidated endpoint response)
interface DashboardData {
  summary: CostSummary;
  byOrganization: OrganizationCost[];
  byModel: ModelCost[];
  byPrompt: PromptCost[];
  byService: ServiceCost[];
  byEncounter: EncounterCost[];
  byOperation: OperationCost[];
  byUser: UserCost[];
  trends: TrendDataPoint[];
  prompts: PromptDefinition[];
}

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_PREFIX = 'ai-costs:';

// Cache utilities
const getCacheKey = (
  startDate: string,
  endDate: string,
  granularity: string,
  enterpriseId: string,
  partnerId: string,
  orgId: string
): string => {
  return `${CACHE_PREFIX}${startDate}_${endDate}_${granularity}_${enterpriseId}_${partnerId}_${orgId}`;
};

const getCachedData = (key: string): DashboardData | null => {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL) {
      sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const setCachedData = (key: string, data: DashboardData): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // sessionStorage full - clear old cache entries
    clearOldCache();
    try {
      sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    } catch {
      // Still full, ignore
    }
  }
};

const clearOldCache = (): void => {
  const keys = Object.keys(sessionStorage).filter(k => k.startsWith(CACHE_PREFIX));
  keys.forEach(key => {
    try {
      const cached = sessionStorage.getItem(key);
      if (cached) {
        const { timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp > CACHE_TTL) {
          sessionStorage.removeItem(key);
        }
      }
    } catch {
      sessionStorage.removeItem(key);
    }
  });
};

// Utility functions
const formatCurrency = (value: number | undefined | null): string => {
  const v = value ?? 0;
  if (v >= 1000) {
    return `$${(v / 1000).toFixed(2)}K`;
  }
  return `$${v.toFixed(4)}`;
};

const formatTokens = (value: number | undefined | null): string => {
  const v = value ?? 0;
  if (v >= 1000000) {
    return `${(v / 1000000).toFixed(2)}M`;
  }
  if (v >= 1000) {
    return `${(v / 1000).toFixed(1)}K`;
  }
  return v.toString();
};

const formatNumber = (value: number | undefined | null): string => {
  return (value ?? 0).toLocaleString();
};

const formatMinutes = (value: number | undefined | null): string => {
  const v = value ?? 0;
  if (v >= 60) {
    const hours = Math.floor(v / 60);
    const mins = Math.round(v % 60);
    return `${hours}h ${mins}m`;
  }
  return `${v.toFixed(1)}m`;
};

const getDateRange = (preset: TimePreset): { startDate: string; endDate: string } => {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0];
  let startDate: string;

  switch (preset) {
    case 'thisMonth':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      break;
    case 'lastMonth':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      break;
    case 'last3Months':
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0];
      break;
    case 'thisYear':
      startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      break;
    case 'lastYear':
      startDate = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
      break;
    default:
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  }

  return { startDate, endDate };
};

// Category colors for prompts
const categoryColors: Record<string, { bg: string; text: string; ring: string }> = {
  summarization: { bg: 'bg-violet-100', text: 'text-violet-700', ring: 'ring-violet-500/20' },
  coding: { bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-500/20' },
  extraction: { bg: 'bg-amber-100', text: 'text-amber-700', ring: 'ring-amber-500/20' },
  identification: { bg: 'bg-sky-100', text: 'text-sky-700', ring: 'ring-sky-500/20' }
};

// Model colors
const modelColors: Record<string, string> = {
  'gpt-4o-mini': '#8B5CF6',
  'gpt-4o': '#EC4899',
  'gpt-4': '#F59E0B',
  'gpt-3.5-turbo': '#10B981'
};

// Access Denied Component
const AccessDenied: React.FC<{ reason: 'role' | 'permission' }> = ({ reason }) => (
  <PageTransition className="flex items-center justify-center min-h-[60vh]">
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center max-w-md"
    >
      <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-6">
        <LockClosedIcon className="w-10 h-10 text-purple-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Access Denied</h2>
      <p className="text-gray-600 mb-6">
        {reason === 'role'
          ? 'AI Cost Analytics is only available to platform administrators.'
          : 'You need the platform.ai_analytics.view permission to access this dashboard. Contact a system administrator to request access.'}
      </p>
      <div className="inline-flex items-center gap-2 text-sm text-purple-600 bg-purple-50 px-4 py-2 rounded-full">
        <ExclamationTriangleIcon className="w-4 h-4" />
        Required: platform.ai_analytics.view
      </div>
    </motion.div>
  </PageTransition>
);

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  color: 'purple' | 'pink' | 'emerald' | 'amber';
  delay?: number;
}> = ({ title, value, subtitle, icon: Icon, trend, color, delay = 0 }) => {
  const colorClasses = {
    purple: 'from-purple-500 to-purple-600',
    pink: 'from-pink-500 to-pink-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="relative group"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg shadow-purple-500/5 overflow-hidden">
        {/* Background gradient accent */}
        <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colorClasses[color]} opacity-5 rounded-full -translate-y-1/2 translate-x-1/2`} />

        <div className="flex items-start justify-between relative">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-500 tracking-wide uppercase">{title}</p>
            <p className="mt-2 text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {value}
            </p>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
            )}
            {trend && (
              <div className={`mt-3 inline-flex items-center gap-1 text-sm font-medium ${trend.isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                {trend.isPositive ? (
                  <ArrowTrendingUpIcon className="w-4 h-4" />
                ) : (
                  <ArrowTrendingDownIcon className="w-4 h-4" />
                )}
                {Math.abs(trend.value).toFixed(1)}% vs last period
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Simple Bar Chart Component
const BarChart: React.FC<{
  data: { label: string; value: number; color?: string }[];
  maxValue?: number;
  formatValue?: (v: number) => string;
  horizontal?: boolean;
}> = ({ data, maxValue, formatValue = formatCurrency, horizontal = false }) => {
  const max = maxValue || Math.max(...data.map(d => d.value));

  if (horizontal) {
    return (
      <div className="space-y-3">
        {data.map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="group"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700 truncate max-w-[60%]">{item.label}</span>
              <span className="text-sm font-semibold text-gray-900">{formatValue(item.value)}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(item.value / max) * 100}%` }}
                transition={{ delay: index * 0.05 + 0.2, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="h-full rounded-full"
                style={{ backgroundColor: item.color || '#8B5CF6' }}
              />
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-end justify-between gap-2 h-48">
      {data.map((item, index) => (
        <motion.div
          key={item.label}
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{ opacity: 1, scaleY: 1 }}
          transition={{ delay: index * 0.05, duration: 0.4 }}
          style={{ originY: 1 }}
          className="flex-1 flex flex-col items-center"
        >
          <div className="relative w-full flex justify-center mb-2">
            <motion.div
              className="w-full max-w-[40px] rounded-t-lg"
              style={{
                height: `${Math.max((item.value / max) * 160, 8)}px`,
                backgroundColor: item.color || '#8B5CF6'
              }}
              whileHover={{ scale: 1.05 }}
            />
          </div>
          <span className="text-xs text-gray-500 text-center truncate w-full">{item.label}</span>
        </motion.div>
      ))}
    </div>
  );
};

// Donut Chart Component
const DonutChart: React.FC<{
  data: { label: string; value: number; color: string }[];
  size?: number;
}> = ({ data, size = 180 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const strokeWidth = 24;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  let currentOffset = 0;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth={strokeWidth}
        />
        {/* Data segments */}
        {data.map((segment, index) => {
          const segmentLength = (segment.value / total) * circumference;
          const offset = currentOffset;
          currentOffset += segmentLength;

          return (
            <motion.circle
              key={segment.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
              strokeDashoffset={-offset}
              initial={{ strokeDasharray: `0 ${circumference}` }}
              animate={{ strokeDasharray: `${segmentLength} ${circumference - segmentLength}` }}
              transition={{ delay: index * 0.1, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-gray-900">{formatCurrency(total)}</span>
        <span className="text-xs text-gray-500">Total Cost</span>
      </div>
    </div>
  );
};

// Trend Line Chart Component
const TrendChart: React.FC<{
  data: TrendDataPoint[];
  height?: number;
}> = ({ data, height = 200 }) => {
  if (!data.length) return null;

  const maxCost = Math.max(...data.map(d => d.totalCost));
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - (d.totalCost / maxCost) * 100
  }));

  const pathD = points.length > 1
    ? `M ${points[0].x} ${points[0].y} ${points.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ')}`
    : '';

  return (
    <div className="relative" style={{ height }}>
      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(y => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="100"
            y2={y}
            stroke="#e5e7eb"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {/* Area fill */}
        <motion.path
          d={`${pathD} L 100 100 L 0 100 Z`}
          fill="url(#areaGradient)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        />
        {/* Line */}
        <motion.path
          d={pathD}
          fill="none"
          stroke="#8B5CF6"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
        {/* Gradient definition */}
        <defs>
          <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      {/* X-axis labels */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 -mb-6">
        {data.filter((_, i) => i % Math.ceil(data.length / 5) === 0).map((d, i) => (
          <span key={i}>{d.date}</span>
        ))}
      </div>
    </div>
  );
};

// Stacked Area Chart for Token Consumption
const TokenStackedChart: React.FC<{
  data: TrendDataPoint[];
  height?: number;
}> = ({ data, height = 200 }) => {
  if (!data.length) return null;

  const maxTokens = Math.max(...data.map(d => (d.promptTokens || 0) + (d.completionTokens || 0)));
  if (maxTokens === 0) return null;

  // Calculate points for both areas
  const promptPoints = data.map((d, i) => ({
    x: data.length > 1 ? (i / (data.length - 1)) * 100 : 50,
    y: 100 - ((d.promptTokens || 0) / maxTokens) * 100
  }));

  const totalPoints = data.map((d, i) => ({
    x: data.length > 1 ? (i / (data.length - 1)) * 100 : 50,
    y: 100 - (((d.promptTokens || 0) + (d.completionTokens || 0)) / maxTokens) * 100
  }));

  // Build paths
  const buildPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return `M ${points[0]?.x || 0} ${points[0]?.y || 100}`;
    return `M ${points[0].x} ${points[0].y} ${points.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ')}`;
  };

  const promptPath = buildPath(promptPoints);
  const totalPath = buildPath(totalPoints);

  // Build area paths (closed shapes)
  const promptAreaPath = `${promptPath} L 100 100 L 0 100 Z`;
  const completionAreaPath = data.length > 1
    ? `${totalPath} L ${promptPoints[promptPoints.length - 1].x} ${promptPoints[promptPoints.length - 1].y} ${[...promptPoints].reverse().slice(1).map(p => `L ${p.x} ${p.y}`).join(' ')} Z`
    : '';

  const totalPromptTokens = data.reduce((sum, d) => sum + (d.promptTokens || 0), 0);
  const totalCompletionTokens = data.reduce((sum, d) => sum + (d.completionTokens || 0), 0);

  return (
    <div className="relative" style={{ height }}>
      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(y => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="100"
            y2={y}
            stroke="#e5e7eb"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* Completion tokens area (on top of prompt) */}
        {completionAreaPath && (
          <motion.path
            d={completionAreaPath}
            fill="#EC4899"
            fillOpacity="0.6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          />
        )}

        {/* Prompt tokens area (bottom) */}
        <motion.path
          d={promptAreaPath}
          fill="#8B5CF6"
          fillOpacity="0.6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        />

        {/* Total line (top edge) */}
        <motion.path
          d={totalPath}
          fill="none"
          stroke="#EC4899"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        />

        {/* Prompt line (middle edge) */}
        <motion.path
          d={promptPath}
          fill="none"
          stroke="#8B5CF6"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
      </svg>

      {/* X-axis labels */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 -mb-6">
        {data.filter((_, i) => i % Math.ceil(data.length / 5) === 0).map((d, i) => (
          <span key={i}>{d.date}</span>
        ))}
      </div>

      {/* Legend */}
      <div className="absolute top-2 right-2 flex flex-col gap-1 text-xs bg-white/80 backdrop-blur-sm rounded-lg p-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-purple-500/60" />
          <span className="text-gray-600">Input: {formatTokens(totalPromptTokens)}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-pink-500/60" />
          <span className="text-gray-600">Output: {formatTokens(totalCompletionTokens)}</span>
        </div>
      </div>
    </div>
  );
};

// Prompt Card Component
const PromptCard: React.FC<{
  prompt: PromptDefinition;
  index: number;
  onViewPrompt: (prompt: PromptDefinition) => void;
}> = ({ prompt, index, onViewPrompt }) => {
  const colors = categoryColors[prompt.category] || categoryColors.summarization;
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300"
    >
      <div
        className="p-5 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text} ring-1 ${colors.ring}`}>
                {prompt.category}
              </span>
              <span className="text-xs text-gray-400">{prompt.model}</span>
            </div>
            <h3 className="font-semibold text-gray-900">{prompt.name}</h3>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{prompt.description}</p>
          </div>
          {prompt.stats && (
            <div className="text-right ml-4">
              <p className="text-lg font-bold text-purple-600">{formatCurrency(prompt.stats.totalCost)}</p>
              <p className="text-xs text-gray-500">{formatNumber(prompt.stats.totalRequests)} calls</p>
            </div>
          )}
        </div>

        {/* Stats row */}
        {prompt.stats && (
          <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500">Avg Input</p>
              <p className="font-medium text-gray-900">{formatTokens(prompt.stats.avgPromptTokens ?? 0)} tokens</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Avg Output</p>
              <p className="font-medium text-gray-900">{formatTokens(prompt.stats.avgCompletionTokens ?? 0)} tokens</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Efficiency</p>
              <p className="font-medium text-gray-900">
                {(prompt.stats.avgPromptTokens ?? 0) > 0
                  ? ((prompt.stats.avgCompletionTokens ?? 0) / (prompt.stats.avgPromptTokens ?? 1)).toFixed(2)
                  : 'N/A'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-2 bg-gray-50/50 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Service File:</span>
                  <p className="font-mono text-xs text-gray-700 mt-1">{prompt.service}</p>
                </div>
                <div>
                  <span className="text-gray-500">Estimated Tokens:</span>
                  <p className="text-gray-700 mt-1">
                    {prompt.estimatedInputTokens} in / {prompt.estimatedOutputTokens} out
                  </p>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewPrompt(prompt);
                }}
                className="mt-4 w-full px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition-colors flex items-center justify-center gap-2"
              >
                <DocumentTextIcon className="w-4 h-4" />
                View Prompt Details
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Component
const AICostDashboard: React.FC = () => {
  const { user, hasPermission } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timePreset, setTimePreset] = useState<TimePreset>('thisMonth');
  const [granularity, setGranularity] = useState<Granularity>('daily');

  // Hierarchical scope filter (enterprise → partner → organization), URL-synced.
  // We use useFilterParams (same hook used by PlatformPartners/PlatformEnterprises)
  // so any filtered view is shareable via link.
  const { filters: scope, setFilter: setScopeFilter, setFilters: setScopeFilters } = useFilterParams<{
    enterpriseId: string;
    partnerId: string;
    organizationId: string;
  }>({
    schema: {
      enterpriseId: { type: 'string', default: 'all' },
      partnerId: { type: 'string', default: 'all' },
      organizationId: { type: 'string', default: 'all' },
    },
    labels: {
      enterpriseId: 'Enterprise',
      partnerId: 'Channel Partner',
      organizationId: 'Organization',
    },
  });

  const [hierarchy, setHierarchy] = useState<FilterHierarchy | null>(null);

  // Back-compat alias: many cache/api call sites use selectedOrganization.
  // It collapses to the effective organization scope (or 'all').
  const selectedOrganization = scope.organizationId || 'all';
  const selectedPartner = scope.partnerId || 'all';
  const selectedEnterprise = scope.enterpriseId || 'all';

  // Custom date range state
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<'csv' | 'xlsx' | null>(null);

  // Data state
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [organizationCosts, setOrganizationCosts] = useState<OrganizationCost[]>([]);
  const [modelCosts, setModelCosts] = useState<ModelCost[]>([]);
  const [promptCosts, setPromptCosts] = useState<PromptCost[]>([]);
  const [serviceCosts, setServiceCosts] = useState<ServiceCost[]>([]);
  const [encounterCosts, setEncounterCosts] = useState<EncounterCost[]>([]);
  const [operationCosts, setOperationCosts] = useState<OperationCost[]>([]);
  const [userCosts, setUserCosts] = useState<UserCost[]>([]);
  const [trends, setTrends] = useState<TrendDataPoint[]>([]);
  const [prompts, setPrompts] = useState<PromptDefinition[]>([]);

  // Modal state
  const [selectedPrompt, setSelectedPrompt] = useState<PromptDefinition | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('finance');

  // User cost table sorting
  const [userSortState, setUserSortState] = useState<SortState>({ column: 'totalCost', direction: 'desc' });

  // Check permissions
  const isSuperadmin = user?.role === 'superadmin';
  const hasAIAnalyticsPermission = hasPermission('platform.ai_analytics.view');

  // Fetch the full filter hierarchy (enterprises → partners → organizations) once.
  // Used to populate the cascading filter dropdowns.
  useEffect(() => {
    if (!isSuperadmin || !hasAIAnalyticsPermission) return;

    const fetchHierarchy = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      try {
        const response = await fetch(`${API_BASE_URL}/v1/platform/ai-costs/filter-hierarchy`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setHierarchy(data.data as FilterHierarchy);
          }
        }
      } catch (err) {
        console.error('Failed to fetch filter hierarchy:', err);
      }
    };

    fetchHierarchy();
  }, [isSuperadmin, hasAIAnalyticsPermission]);

  // Derived: visible partners depend on selected enterprise.
  // Derived: visible organizations depend on selected partner (or all of the enterprise).
  const visiblePartners = useMemo<HierarchyPartner[]>(() => {
    if (!hierarchy) return [];
    if (selectedEnterprise === 'all') {
      const all = hierarchy.enterprises.flatMap((e) => e.partners);
      return [...all, ...hierarchy.orphanPartners];
    }
    const ent = hierarchy.enterprises.find((e) => e.id === selectedEnterprise);
    return ent ? ent.partners : [];
  }, [hierarchy, selectedEnterprise]);

  const visibleOrganizations = useMemo<HierarchyOrganization[]>(() => {
    if (!hierarchy) return [];
    if (selectedPartner !== 'all') {
      const partner = visiblePartners.find((p) => p.id === selectedPartner);
      return partner ? partner.organizations : [];
    }
    if (selectedEnterprise !== 'all') {
      return visiblePartners.flatMap((p) => p.organizations);
    }
    // No scope selected → flatten all
    const all = hierarchy.enterprises.flatMap((e) => e.partners.flatMap((p) => p.organizations));
    const orphan = hierarchy.orphanPartners.flatMap((p) => p.organizations);
    return [...all, ...orphan];
  }, [hierarchy, selectedEnterprise, selectedPartner, visiblePartners]);

  // Reset child selectors when parent changes and the previous child is no longer in scope.
  useEffect(() => {
    if (selectedPartner !== 'all' && !visiblePartners.some((p) => p.id === selectedPartner)) {
      setScopeFilters({ partnerId: 'all', organizationId: 'all' });
    }
  }, [selectedEnterprise, selectedPartner, visiblePartners, setScopeFilters]);

  useEffect(() => {
    if (selectedOrganization !== 'all' && !visibleOrganizations.some((o) => o.id === selectedOrganization)) {
      setScopeFilter('organizationId', 'all');
    }
  }, [selectedPartner, selectedOrganization, visibleOrganizations, setScopeFilter]);

  // AbortController ref for request deduplication
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch data using consolidated endpoint with caching
  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // Calculate date range
    let startDate: string;
    let endDate: string;
    if (timePreset === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    } else {
      const dates = getDateRange(timePreset);
      startDate = dates.startDate;
      endDate = dates.endDate;
    }

    const cacheKey = getCacheKey(
      startDate,
      endDate,
      granularity,
      selectedEnterprise,
      selectedPartner,
      selectedOrganization
    );

    // Check cache first
    const cached = getCachedData(cacheKey);
    if (cached) {
      setSummary(cached.summary);
      setOrganizationCosts(cached.byOrganization);
      setModelCosts(cached.byModel);
      setPromptCosts(cached.byPrompt);
      setServiceCosts(cached.byService);
      setEncounterCosts(cached.byEncounter || []);
      setOperationCosts(cached.byOperation || []);
      setUserCosts(cached.byUser || []);
      setTrends(cached.trends);
      setPrompts(cached.prompts);
      setLoading(false);
      return;
    }

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    // Build hierarchical scope params. The backend resolves precedence
    // (organizationId > partnerId > enterpriseId); send all that are set.
    const scopeParams: string[] = [];
    if (selectedEnterprise !== 'all') scopeParams.push(`enterpriseId=${selectedEnterprise}`);
    if (selectedPartner !== 'all') scopeParams.push(`partnerId=${selectedPartner}`);
    if (selectedOrganization !== 'all') scopeParams.push(`organizationId=${selectedOrganization}`);
    const scopeQuery = scopeParams.length > 0 ? `&${scopeParams.join('&')}` : '';

    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/platform/ai-costs/dashboard?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}${scopeQuery}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal: abortControllerRef.current.signal
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const data = result.data as DashboardData;

          // Update state
          setSummary(data.summary);
          setOrganizationCosts(data.byOrganization);
          setModelCosts(data.byModel);
          setPromptCosts(data.byPrompt);
          setServiceCosts(data.byService);
          setEncounterCosts(data.byEncounter || []);
          setOperationCosts(data.byOperation || []);
          setUserCosts(data.byUser || []);
          setTrends(data.trends);
          setPrompts(data.prompts);

          // Cache the data
          setCachedData(cacheKey, data);
        }
      } else {
        setError('Failed to load AI cost analytics. Please try again.');
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error fetching AI cost data:', err);
      setError('Failed to load AI cost analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [timePreset, granularity, selectedEnterprise, selectedPartner, selectedOrganization, customStartDate, customEndDate]);

  // Fetch data effect
  useEffect(() => {
    if (!isSuperadmin || !hasAIAnalyticsPermission) {
      setLoading(false);
      return;
    }

    fetchDashboardData();

    // Cleanup: abort on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isSuperadmin, hasAIAnalyticsPermission, fetchDashboardData]);

  // Handle CSV / XLSX export
  const handleExport = async (format: 'csv' | 'xlsx' = 'csv') => {
    if (exportingFormat) return;
    const label = format === 'xlsx' ? 'Excel' : 'CSV';
    const token = localStorage.getItem('accessToken');
    if (!token) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }

    // Use custom dates if preset is 'custom', otherwise use preset dates
    let startDate: string;
    let endDate: string;
    if (timePreset === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    } else {
      const dates = getDateRange(timePreset);
      startDate = dates.startDate;
      endDate = dates.endDate;
    }
    const scopeParams: string[] = [];
    if (selectedEnterprise !== 'all') scopeParams.push(`enterpriseId=${selectedEnterprise}`);
    if (selectedPartner !== 'all') scopeParams.push(`partnerId=${selectedPartner}`);
    if (selectedOrganization !== 'all') scopeParams.push(`organizationId=${selectedOrganization}`);
    const scopeQuery = scopeParams.length > 0 ? `&${scopeParams.join('&')}` : '';

    setExportingFormat(format);
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/platform/ai-costs/export?startDate=${startDate}&endDate=${endDate}&format=${format}${scopeQuery}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-costs-${startDate}-${endDate}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error(`Failed to export ${label}. Please try again.`);
      }
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(`Failed to export ${label}. Please try again.`);
    } finally {
      setExportingFormat(null);
    }
  };

  // Prepare chart data
  const serviceChartData = useMemo(() =>
    serviceCosts.map(s => ({
      label: s.service,
      value: s.totalCost,
      color: s.service === 'AZURE_OPENAI' ? '#8B5CF6' : s.service === 'AZURE_SPEECH' ? '#EC4899' : '#10B981'
    })),
    [serviceCosts]
  );

  const modelChartData = useMemo(() =>
    modelCosts.map(m => ({
      label: m.model,
      value: m.totalCost,
      color: modelColors[m.model] || '#8B5CF6'
    })),
    [modelCosts]
  );

  const orgChartData = useMemo(() =>
    organizationCosts.slice(0, 10).map(o => ({
      label: o.organizationName || o.organizationId.slice(0, 8),
      value: o.totalCost,
      color: '#8B5CF6'
    })),
    [organizationCosts]
  );

  // User cost table - sorted data
  const sortedUserCosts = useMemo(() => {
    if (!userSortState.column || !userSortState.direction) return userCosts;

    return [...userCosts].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (userSortState.column) {
        case 'userName':
          aVal = a.userName;
          bVal = b.userName;
          break;
        case 'totalCost':
          aVal = a.totalCost;
          bVal = b.totalCost;
          break;
        case 'encounterCount':
          aVal = a.encounterCount;
          bVal = b.encounterCount;
          break;
        case 'avgCostPerEncounter':
          aVal = a.avgCostPerEncounter;
          bVal = b.avgCostPerEncounter;
          break;
        case 'audioMinutes':
          aVal = a.audioMinutes;
          bVal = b.audioMinutes;
          break;
        case 'percentOfTotal':
          aVal = a.percentOfTotal;
          bVal = b.percentOfTotal;
          break;
        default:
          return 0;
      }

      if (aVal == null) return userSortState.direction === 'asc' ? 1 : -1;
      if (bVal == null) return userSortState.direction === 'asc' ? -1 : 1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal as string)
        : Number(aVal) - Number(bVal);
      return userSortState.direction === 'asc' ? cmp : -cmp;
    });
  }, [userCosts, userSortState]);

  // User cost table columns
  const userCostColumns: ColumnDefinition<UserCost>[] = useMemo(() => [
    {
      id: 'userName',
      header: 'User',
      accessor: (user) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-medium">
            {user.userName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="font-medium text-gray-900">{user.userName}</div>
            <div className="text-xs text-gray-500 capitalize">{user.userRole}</div>
          </div>
        </div>
      ),
      sortable: true,
      sortKey: 'userName'
    },
    {
      id: 'totalCost',
      header: 'Cost',
      accessor: (user) => (
        <span className="font-semibold text-purple-600">{formatCurrency(user.totalCost)}</span>
      ),
      sortable: true,
      sortKey: 'totalCost',
      align: 'right' as const
    },
    {
      id: 'encounterCount',
      header: 'Encounters',
      accessor: (user) => (
        <span className="text-gray-600">{formatNumber(user.encounterCount)}</span>
      ),
      sortable: true,
      sortKey: 'encounterCount',
      align: 'right' as const,
      hideOnMobile: true
    },
    {
      id: 'avgCostPerEncounter',
      header: 'Avg/Enc',
      accessor: (user) => (
        <span className="text-gray-600">{formatCurrency(user.avgCostPerEncounter)}</span>
      ),
      sortable: true,
      sortKey: 'avgCostPerEncounter',
      align: 'right' as const,
      hideOnTablet: true
    },
    {
      id: 'audioMinutes',
      header: 'Audio',
      accessor: (user) => (
        <span className="text-gray-600">{formatMinutes(user.audioMinutes)}</span>
      ),
      sortable: true,
      sortKey: 'audioMinutes',
      align: 'right' as const,
      hideOnTablet: true
    },
    {
      id: 'percentOfTotal',
      header: '% of Total',
      accessor: (user) => (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
              style={{ width: `${Math.min(user.percentOfTotal, 100)}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 w-10 text-right">
            {user.percentOfTotal.toFixed(1)}%
          </span>
        </div>
      ),
      sortable: true,
      sortKey: 'percentOfTotal',
      minWidth: '120px'
    }
  ], []);

  // Access control
  if (!isSuperadmin) {
    return <AccessDenied reason="role" />;
  }

  if (!hasAIAnalyticsPermission) {
    return <AccessDenied reason="permission" />;
  }

  // Loading state
  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <div className="h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full" />
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  // Error state
  if (error) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="space-y-8" stagger staggerDelay={0.03}>
      {/* Header */}
      <PageTransitionItem>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
                <SparklesIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AI Cost Analytics</h1>
                <p className="text-gray-500 text-sm">Monitor Azure OpenAI pipeline costs and prompt performance</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Hierarchical Scope: Enterprise → Channel Partner → Organization */}
            <select
              value={selectedEnterprise}
              onChange={(e) => {
                // Changing enterprise resets downstream filters so we don't keep stale child IDs.
                setScopeFilters({
                  enterpriseId: e.target.value,
                  partnerId: 'all',
                  organizationId: 'all',
                });
              }}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              aria-label="Filter by enterprise"
            >
              <option value="all">All Enterprises</option>
              {hierarchy?.enterprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>

            <select
              value={selectedPartner}
              onChange={(e) => {
                setScopeFilters({
                  partnerId: e.target.value,
                  organizationId: 'all',
                });
              }}
              disabled={visiblePartners.length === 0}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Filter by channel partner"
            >
              <option value="all">All Channel Partners</option>
              {visiblePartners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <select
              value={selectedOrganization}
              onChange={(e) => setScopeFilter('organizationId', e.target.value)}
              disabled={visibleOrganizations.length === 0}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Filter by organization"
            >
              <option value="all">All Organizations</option>
              {visibleOrganizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>

            {/* Time Period Selector */}
            <select
              value={timePreset}
              onChange={(e) => {
                const value = e.target.value as TimePreset;
                setTimePreset(value);
                if (value === 'custom') {
                  setShowCustomDatePicker(true);
                  // Initialize custom dates to current month if empty
                  if (!customStartDate || !customEndDate) {
                    const now = new Date();
                    setCustomStartDate(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
                    setCustomEndDate(now.toISOString().split('T')[0]);
                  }
                } else {
                  setShowCustomDatePicker(false);
                }
              }}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="last3Months">Last 3 Months</option>
              <option value="thisYear">This Year</option>
              <option value="lastYear">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>

            {/* Custom Date Range Picker */}
            {showCustomDatePicker && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Export: static label, one interactive segment per format */}
            <div className="inline-flex items-center gap-2.5">
              <span
                id="ai-cost-export-label"
                className="text-xs font-semibold uppercase tracking-wider text-gray-400"
              >
                Export
              </span>
              <div
                role="group"
                aria-labelledby="ai-cost-export-label"
                className="inline-flex items-stretch divide-x divide-gray-200 rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm"
              >
                {([{ format: 'csv', label: 'CSV' }, { format: 'xlsx', label: 'Excel' }] as const).map(({ format, label }) => (
                  <motion.button
                    key={format}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleExport(format)}
                    disabled={exportingFormat !== null}
                    aria-label={`Export as ${label}`}
                    aria-busy={exportingFormat === format}
                    className="group inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-500 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {exportingFormat === format ? (
                      <ArrowPathIcon className="w-4 h-4 text-purple-600 animate-spin" />
                    ) : (
                      <ArrowDownTrayIcon className="w-4 h-4 text-purple-600 group-hover:text-purple-700 transition-colors" />
                    )}
                    {label}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </PageTransitionItem>

      {/* Compact Summary - Always Visible */}
      <PageTransitionItem>
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-lg">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-gray-500">Total Cost</span>
              <span className="text-lg font-bold text-gray-900">{formatCurrency(summary?.totalCost ?? 0)}</span>
              {summary?.comparison?.changePercent != null && (
                <span className={`text-xs font-medium ${summary.comparison.changePercent < 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {summary.comparison.changePercent >= 0 ? '+' : ''}{summary.comparison.changePercent.toFixed(1)}%
                </span>
              )}
            </div>
            <div className="h-6 w-px bg-gray-200 hidden md:block" />
            <div className="flex items-center gap-2">
              <UserGroupIcon className="w-5 h-5 text-pink-600" />
              <span className="text-sm text-gray-500">Encounters</span>
              <span className="text-lg font-bold text-gray-900">{formatNumber(summary?.encounterCount ?? 0)}</span>
            </div>
            <div className="h-6 w-px bg-gray-200 hidden md:block" />
            <div className="flex items-center gap-2">
              <CircleStackIcon className="w-5 h-5 text-emerald-600" />
              <span className="text-sm text-gray-500">Tokens</span>
              <span className="text-lg font-bold text-gray-900">{formatTokens(summary?.totalTokens ?? 0)}</span>
            </div>
            <div className="h-6 w-px bg-gray-200 hidden md:block" />
            <div className="flex items-center gap-2">
              <BoltIcon className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-gray-500">API Calls</span>
              <span className="text-lg font-bold text-gray-900">{formatNumber(summary?.totalRequests ?? 0)}</span>
            </div>
            <div className="h-6 w-px bg-gray-200 hidden md:block" />
            <div className="flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5 text-purple-600" />
              <span className="text-sm text-gray-500">Avg/Encounter</span>
              <span className="text-lg font-bold text-gray-900">{formatCurrency(summary?.avgCostPerAppointment ?? 0)}</span>
            </div>
            <div className="h-6 w-px bg-gray-200 hidden md:block" />
            <div className="flex items-center gap-2">
              <MicrophoneIcon className="w-5 h-5 text-pink-600" />
              <span className="text-sm text-gray-500">Speech</span>
              <span className="text-lg font-bold text-gray-900">{formatCurrency(summary?.speechCost ?? 0)}</span>
            </div>
          </div>
        </div>
      </PageTransitionItem>

      {/* Tab Navigation */}
      <PageTransitionItem>
        <div className="flex items-center justify-center">
          <div className="inline-flex bg-white/80 backdrop-blur-sm rounded-xl p-1 border border-white/50 shadow-lg">
            <button
              onClick={() => setActiveTab('finance')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'finance'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
              }`}
            >
              <CurrencyDollarIcon className="w-4 h-4" />
              Finance
            </button>
            <button
              onClick={() => setActiveTab('engineering')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === 'engineering'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
              }`}
            >
              <CpuChipIcon className="w-4 h-4" />
              Engineering
            </button>
          </div>
        </div>
      </PageTransitionItem>

      {/* ==================== FINANCE TAB ==================== */}
      {activeTab === 'finance' && (
        <>
          {/* Top Users by AI Cost */}
          <PageTransitionItem>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
            >
              <div className="flex items-center gap-2 mb-4">
                <UserGroupIcon className="w-5 h-5 text-purple-600" />
                <h2 className="font-semibold text-gray-900">Top Users by AI Cost</h2>
                <span className="ml-auto text-xs text-gray-500">Top 10 users generating AI costs</span>
              </div>
              <DataTable
                data={sortedUserCosts}
                columns={userCostColumns}
                getRowId={(row) => row.userId}
                loading={false}
                emptyMessage="No user cost data available for this period"
                emptyIcon={<UserGroupIcon className="h-12 w-12 text-gray-300" />}
                sortable
                sortState={userSortState}
                onSortChange={setUserSortState}
                className="border-0 shadow-none rounded-none"
              />
            </motion.div>
          </PageTransitionItem>

          {/* Finance 2x2 Grid */}
          <PageTransitionItem>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost Trend Chart (compact) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/50 shadow-lg"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ArrowTrendingUpIcon className="w-5 h-5 text-purple-600" />
                    <h2 className="font-semibold text-gray-900">Cost Trend</h2>
                  </div>
                  <select
                    value={granularity}
                    onChange={(e) => setGranularity(e.target.value as Granularity)}
                    className="px-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                {trends.length > 0 ? (
                  <TrendChart data={trends} height={180} />
                ) : (
                  <div className="h-[180px] flex items-center justify-center text-gray-500">
                    No trend data available
                  </div>
                )}
              </motion.div>

              {/* Top Encounters (compact - 5 rows) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/50 shadow-lg"
              >
                <div className="flex items-center gap-2 mb-4">
                  <UserGroupIcon className="w-5 h-5 text-purple-600" />
                  <h2 className="font-semibold text-gray-900">Top Encounters</h2>
                </div>
                {encounterCosts.length > 0 ? (
                  <div className="space-y-2">
                    {encounterCosts.slice(0, 5).map((enc, index) => (
                      <motion.div
                        key={enc.encounterId || enc.appointmentId || index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-gray-900 truncate">{enc.patientName || 'Unknown'}</span>
                            <span className="text-xs text-gray-400 shrink-0">
                              {new Date(enc.encounterDate).toLocaleDateString()}
                            </span>
                          </div>
                          <span className="font-bold text-purple-600 shrink-0 ml-2">{formatCurrency(enc.totalCost)}</span>
                        </div>
                        <div className="flex gap-1 h-1.5 rounded-full overflow-hidden bg-gray-200 mt-1.5">
                          {enc.speechCost > 0 && (
                            <div className="bg-pink-500" style={{ width: `${(enc.speechCost / enc.totalCost) * 100}%` }} />
                          )}
                          {enc.openaiCost > 0 && (
                            <div className="bg-purple-500" style={{ width: `${(enc.openaiCost / enc.totalCost) * 100}%` }} />
                          )}
                          {enc.imoCost > 0 && (
                            <div className="bg-emerald-500" style={{ width: `${(enc.imoCost / enc.totalCost) * 100}%` }} />
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[180px] flex items-center justify-center text-gray-500">
                    No encounter data
                  </div>
                )}
              </motion.div>

              {/* Service Breakdown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/50 shadow-lg"
              >
                <div className="flex items-center gap-2 mb-4">
                  <CpuChipIcon className="w-5 h-5 text-purple-600" />
                  <h2 className="font-semibold text-gray-900">Cost by Service</h2>
                </div>
                <div className="flex justify-center mb-3">
                  <DonutChart data={serviceChartData} />
                </div>
                <div className="space-y-1.5">
                  {serviceChartData.map((item) => (
                    <div key={item.label} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-gray-600">{item.label}</span>
                      </div>
                      <span className="font-medium text-gray-900">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Top Organizations */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/50 shadow-lg"
              >
                <div className="flex items-center gap-2 mb-4">
                  <BuildingOfficeIcon className="w-5 h-5 text-purple-600" />
                  <h2 className="font-semibold text-gray-900">Cost by Organization</h2>
                </div>
                <BarChart data={orgChartData} horizontal formatValue={formatCurrency} />
              </motion.div>
            </div>
          </PageTransitionItem>
        </>
      )}

      {/* ==================== ENGINEERING TAB ==================== */}
      {activeTab === 'engineering' && (
        <>
          {/* Engineering Summary Stats */}
          <PageTransitionItem>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Tokens"
                value={summary ? formatTokens(summary.totalTokens ?? 0) : '0'}
                subtitle={summary ? `${formatTokens(summary.promptTokens ?? 0)} in / ${formatTokens(summary.completionTokens ?? 0)} out` : undefined}
                icon={CircleStackIcon}
                color="purple"
                delay={0}
              />
              <StatCard
                title="API Calls"
                value={summary ? formatNumber(summary.totalRequests ?? 0) : '0'}
                subtitle="Total requests"
                icon={BoltIcon}
                color="pink"
                delay={0.05}
              />
              <StatCard
                title="Avg Cost/Call"
                value={summary ? formatCurrency(summary.avgCostPerRequest ?? 0) : '$0.00'}
                subtitle="Per request"
                icon={ChartBarIcon}
                color="emerald"
                delay={0.1}
              />
              <StatCard
                title="Audio Minutes"
                value={summary ? formatMinutes(summary.audioMinutes ?? 0) : '0m'}
                subtitle={summary?.audioMinutes ? `@ $0.36/hour` : undefined}
                icon={ClockIcon}
                color="amber"
                delay={0.15}
              />
            </div>
          </PageTransitionItem>

          {/* Engineering Charts Row: By Model + Token Consumption */}
          <PageTransitionItem>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Model Breakdown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
              >
                <div className="flex items-center gap-2 mb-6">
                  <SparklesIcon className="w-5 h-5 text-purple-600" />
                  <h2 className="font-semibold text-gray-900">Cost by Model</h2>
                </div>
                <BarChart data={modelChartData} />
                <div className="mt-4 space-y-2">
                  {modelCosts.map((model) => (
                    <div key={model.model} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: modelColors[model.model] || '#8B5CF6' }} />
                        <span className="text-gray-600">{model.model}</span>
                      </div>
                      <span className="text-gray-500">{formatNumber(model.totalRequests)} calls</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Token Consumption Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <CircleStackIcon className="w-5 h-5 text-purple-600" />
                    <h2 className="font-semibold text-gray-900">Token Consumption</h2>
                  </div>
                  <div className="text-sm text-gray-500">
                    Input vs Output
                  </div>
                </div>
                <div className="pb-6">
                  {trends.length > 0 ? (
                    <TokenStackedChart data={trends} height={220} />
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-gray-500">
                      No token data available for this period
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </PageTransitionItem>

          {/* Cost by Operation */}
          <PageTransitionItem>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg"
            >
              <div className="flex items-center gap-2 mb-6">
                <WrenchScrewdriverIcon className="w-5 h-5 text-purple-600" />
                <h2 className="font-semibold text-gray-900">Cost by Operation</h2>
              </div>
              {operationCosts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {operationCosts.slice(0, 8).map((op, index) => {
                    const maxCost = Math.max(...operationCosts.map(o => o.totalCost));
                    const opCategoryColors: Record<string, string> = {
                      transcription: '#EC4899',
                      summarization: '#8B5CF6',
                      note_generation: '#10B981',
                      coding: '#F59E0B',
                      other: '#6B7280'
                    };
                    return (
                      <motion.div
                        key={op.operation}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="p-4 bg-gray-50 rounded-xl"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">{op.operationName}</span>
                            <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-gray-100 rounded">
                              {op.service.replace('AZURE_', '')}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{formatCurrency(op.totalCost)}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(op.totalCost / maxCost) * 100}%` }}
                            transition={{ delay: index * 0.05 + 0.2, duration: 0.5 }}
                            className="h-full rounded-full"
                            style={{ backgroundColor: opCategoryColors[op.category] || '#8B5CF6' }}
                          />
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>{formatNumber(op.totalRequests)} calls</span>
                          {op.audioMinutes ? <span>{formatMinutes(op.audioMinutes)} audio</span> : null}
                          {op.avgTokens ? <span>{formatTokens(op.avgTokens)} avg tokens</span> : null}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-500">
                  No operation data available
                </div>
              )}
            </motion.div>
          </PageTransitionItem>

          {/* Prompt Performance */}
          <PageTransitionItem>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <div className="flex items-center gap-2 mb-6">
                <DocumentTextIcon className="w-5 h-5 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-900">Prompt Performance</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {prompts.map((prompt, index) => (
                  <PromptCard key={prompt.id} prompt={prompt} index={index} onViewPrompt={setSelectedPrompt} />
                ))}
              </div>
              {prompts.length === 0 && (
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 border border-white/50 text-center">
                  <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No prompt data available yet</p>
                </div>
              )}
            </motion.div>
          </PageTransitionItem>
        </>
      )}

      {/* Prompt Details Modal */}
      <AnimatePresence>
        {selectedPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPrompt(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedPrompt.name}</h3>
                  <p className="text-sm text-gray-500">{selectedPrompt.id}</p>
                </div>
                <button
                  onClick={() => setSelectedPrompt(null)}
                  className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Description */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Description</h4>
                  <p className="text-gray-700">{selectedPrompt.description}</p>
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Category</h4>
                    <p className="text-gray-900 font-medium capitalize">{selectedPrompt.category}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Model</h4>
                    <p className="text-gray-900 font-medium">{selectedPrompt.model}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Est. Input Tokens</h4>
                    <p className="text-gray-900 font-medium">{formatTokens(selectedPrompt.estimatedInputTokens)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Est. Output Tokens</h4>
                    <p className="text-gray-900 font-medium">{formatTokens(selectedPrompt.estimatedOutputTokens)}</p>
                  </div>
                </div>

                {/* Service File */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Service File</h4>
                  <code className="block bg-gray-900 text-green-400 rounded-lg p-4 text-sm font-mono">
                    {selectedPrompt.service}
                  </code>
                </div>

                {/* Usage Stats */}
                {selectedPrompt.stats && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-3">Usage Statistics</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-600">{formatCurrency(selectedPrompt.stats.totalCost)}</p>
                        <p className="text-xs text-gray-500">Total Cost</p>
                      </div>
                      <div className="bg-pink-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-pink-600">{formatNumber(selectedPrompt.stats.totalRequests)}</p>
                        <p className="text-xs text-gray-500">Total Calls</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-emerald-600">{formatTokens(selectedPrompt.stats.avgPromptTokens)}</p>
                        <p className="text-xs text-gray-500">Avg Input</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-600">{formatTokens(selectedPrompt.stats.avgCompletionTokens)}</p>
                        <p className="text-xs text-gray-500">Avg Output</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setSelectedPrompt(null)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageTransition>
  );
};

export default AICostDashboard;
